/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { NoteIcon, VolumeMaxIcon } from '@coursera/cds-icons';

import { isEmbeddedAudio, isEmbeddedImage, isEmbeddedVideo } from 'bundles/assess-common/utils/embeddedContentUtils';

import _t from 'i18n!nls/assess-common';

import VideoPreviewAtTimestamp from './VideoPreviewAtTimestamp';

type Props = {
  fileUrl: string;
};

const styles = {
  container: () => css`
    background-color: var(--cds-color-neutral-background-primary);
    border-radius: var(--cds-border-radius-100);
    display: flex;
    align-items: center;
    justify-content: center;
    width: 110px;
    height: 75px;
    overflow: hidden;
  `,

  icon: () => css`
    color: var(--cds-color-neutral-primary);
  `,

  preview: () => css`
    min-width: 100%;
    min-height: 100%;
    object-fit: cover;
  `,

  videoPreview: () => css`
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100%;
    height: 100%;
    overflow: hidden;

    video {
      min-width: 100%;
      min-height: 100%;
      object-fit: cover;
    }
  `,
};

const UploadedFilePreview = ({ fileUrl }: Props) => {
  // default icon for no preview
  let previewComponent = <NoteIcon css={styles.icon} data-testid="uploaded-file-default-preview" />;

  if (isEmbeddedImage(fileUrl)) {
    previewComponent = (
      <img src={fileUrl} alt={_t('File preview')} css={styles.preview} data-testid="uploaded-image-preview" />
    );
  } else if (isEmbeddedVideo(fileUrl)) {
    previewComponent = <VideoPreviewAtTimestamp videoUrl={fileUrl} timestampSeconds={0} css={styles.videoPreview} />;
  } else if (isEmbeddedAudio(fileUrl)) {
    previewComponent = <VolumeMaxIcon css={styles.icon} data-testid="uploaded-audio-preview" />;
  }

  return (
    <div css={styles.container} data-testid="uploaded-file-preview">
      {previewComponent}
    </div>
  );
};

export default UploadedFilePreview;
